import React, {useMemo} from 'react';
import {
    Box,
    Paper,
    Stack,
    Step,
    StepButton,
    StepLabel,
    Stepper,
    Button,
    useTheme,
    Tabs,
    Tab,
} from '@mui/material';
import StepConnector, {stepConnectorClasses} from '@mui/material/StepConnector';
import {StepIconProps} from '@mui/material/StepIcon';
import {styled} from '@mui/material/styles';
import {useSearchParams} from 'react-router-dom';
import useMediaQuery from "@mui/material/useMediaQuery";

export interface StepButtonConfig {
    label: string;
    onClick?: (goToStep: (index: number) => void) => void;
    variant?: 'text' | 'outlined' | 'contained';
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

export interface StepConfig {
    label: string;
    labelShort?: string;
    key: string;
    component: React.ReactElement;
    icon: React.ReactElement;
    leftActions?: StepButtonConfig[];
    rightActions?: StepButtonConfig[];
}

interface TimeLineStepperProps {
    steps: StepConfig[];
    handleStepChange?: (step: string) => void;
}

const CustomConnector = styled(StepConnector)(({theme}) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 19,
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            backgroundColor: theme.palette.primary.main,
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            backgroundColor: theme.palette.primary.main,
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 3,
        border: 0,
        backgroundColor: theme.palette.grey[400],
    },
}));

const CustomStepIconRoot = styled('div')<{
    ownerState: { completed?: boolean; active?: boolean };
}>(({theme, ownerState}) => ({
    backgroundColor: ownerState.active || ownerState.completed
        ? theme.palette.primary.main
        : theme.palette.grey[400],
    zIndex: 1,
    color: '#fff',
    width: 40,
    height: 40,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
}));

function CustomStepIcon(props: StepIconProps & { icons: React.ReactElement[] }) {
    const {active, completed, className, icon, icons} = props;
    const index = Number(icon) - 1;
    return (
        <CustomStepIconRoot ownerState={{completed, active}} className={className}>
            {icons[index]}
        </CustomStepIconRoot>
    );
}

const TimeLineStepper: React.FC<TimeLineStepperProps> = ({steps, handleStepChange}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchParams, setSearchParams] = useSearchParams();
    const urlKey = searchParams.get('step');
    const stepKeys = steps.map(step => step.key);
    const stepKey = (urlKey && stepKeys.includes(urlKey)) ? urlKey : steps[0].key;

    const activeStep = steps.findIndex((s) => s.key === stepKey);
    const step = steps[activeStep];
    const activeComponent = useMemo(() => step.component, [step]);

    const goToStep = (index: number) => {
        setSearchParams({step: steps[index].key});
        if (handleStepChange) {
            handleStepChange(steps[index].key);
        }
    };

    return (
        <Stack direction="column" spacing={2}
               sx={{minHeight: '78vh', pb: isMobile ? 4 : 2}}>
            {!isMobile ? (
                <Stepper activeStep={activeStep} alternativeLabel connector={<CustomConnector/>}>
                    {steps.map((step, index) => (
                        <Step key={step.key} disabled={false} completed={index < activeStep}>
                            <StepButton onClick={() => goToStep(index)}>
                                <StepLabel
                                    StepIconComponent={(props) => (
                                        <CustomStepIcon {...props} icons={steps.map((s) => s.icon)}/>
                                    )}
                                >
                                    {step.label}
                                </StepLabel>
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>
            ) : (
                <Tabs
                    value={activeStep}
                    onChange={(_, index) => goToStep(index)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{
                        minHeight: 32,
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: 'background.paper',
                        zIndex: 1000,
                        borderTop: 1,
                        borderColor: 'divider',
                    }}
                >
                    {steps.map((step, index) => (
                        <Tab
                            key={step.key}
                            label={step.labelShort ?? step.label}
                            icon={step.icon}
                            iconPosition="top"
                            sx={{
                                color: index === activeStep ? 'primary.main' : 'text.secondary',
                                borderTopLeftRadius: 10,
                                borderTopRightRadius: 10,
                                border: 0.1,
                                borderColor: "divider",
                                borderBottom: index < activeStep ? "5px solid green" : 'none',
                                minHeight: 32,
                                mx: 0.5,
                                p: 0.5
                            }}
                        />
                    ))}
                </Tabs>
            )}

            <Paper elevation={isMobile ? 0 : 3}
                   sx={{flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'auto'}}>
                <Box p={isMobile ? 0 : 2} flexGrow={1} overflow="hidden"
                     sx={{maxWidth: '100%'}}>
                    {activeComponent}
                </Box>

                {((step.leftActions && step.leftActions.length !== 0) || (step.rightActions && step.rightActions.length !== 0)) && (
                    <Box
                        mt="auto"
                        display="flex"
                        flexDirection={{sm: 'row', xs: 'column-reverse'}}
                        justifyContent="space-between"
                        position="sticky"
                        bgcolor="background.paper"
                        p={2}
                        gap={isMobile ? 1 : 0}
                    >
                        <Box display="flex" gap={1}>
                            {step.leftActions?.map((action, idx) => (
                                <Button
                                    key={idx}
                                    variant={action.variant ?? 'contained'}
                                    color={action.color ?? 'primary'}
                                    onClick={() => action.onClick?.(goToStep)}
                                    fullWidth={isMobile}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </Box>
                        <Box display="flex" gap={1}>
                            {step.rightActions?.map((action, idx) => (
                                <Button
                                    key={idx}
                                    variant={action.variant ?? 'contained'}
                                    color={action.color ?? 'primary'}
                                    onClick={() => action.onClick?.(goToStep)}
                                    fullWidth={isMobile}
                                >
                                    {action.label}
                                </Button>
                            ))}
                        </Box>
                    </Box>
                )}
            </Paper>
        </Stack>
    );
};

export default TimeLineStepper;
